<script setup lang="ts">
import {computed, ref} from "vue";
import ContestsSection from "@/contest/components/ContestsSection.vue";
import ContestsList from "@/contest/components/ContestsList.vue";
import useContests from "@/contest/composables/useContests";
import {storeToRefs} from "pinia";
import {useSystemStore} from "@/stores/system.store";
import BaseButton from "@/core/components/base/BaseButton.vue";
import {Users, Trophy} from "@lucide/vue";
import {Tabs, TabsContent, TabsList, TabsTrigger} from "@/core/components/ui/tabs";

const {environment} = storeToRefs(useSystemStore());

const contestFilter = ref<"upcoming" | "ended" | null>(null);
const {data: contests, isLoading, mutate} = useContests(contestFilter);

const hasGrouping = computed(() => {
    return !!environment.value?.entity?.grouping;
});

const filters: { label: string; value: null | "upcoming" | "ended" }[] = [
    {label: "En-cours", value: null},
    {label: "À venir", value: "upcoming"},
    {label: "Terminés", value: "ended"},
];
</script>

<template>
  <div class="container p-8 mx-auto space-y-8">
    <div class="bg-white rounded-3xl shadow-xl p-8 border border-gray-100">
      <div class="mb-8 text-center">
        <div class="inline-flex items-center justify-center w-16 h-16 gradient rounded-2xl mb-4">
          <Trophy class="w-8 h-8 text-white" />
        </div>
        <h2 class="text-3xl font-bold text-gray-900 mb-3">
          Challenges
        </h2>
        <p class="text-lg text-gray-600 max-w-2xl mx-auto">
          Remportez des récompenses en relevant les challenges du moment
        </p>
        <div class="w-24 h-1 gradient rounded-full mx-auto mt-4" />
      </div>
      <Tabs default-value="carousel" class="w-full">
        <div class="flex items-center justify-between gap-4 flex-wrap mb-4">
          <div class="flex flex-row gap-2">
            <div
              v-for="filter in filters"
              :key="filter.label"
              class="px-3 py-1 rounded-full cursor-pointer text-sm font-medium border transition-colors"
              :class="[
                'hover:bg-gray-100',
                contestFilter === filter.value ? 'bg-primary/10 text-primary border-primary/20' : 'bg-gray-200 text-gray-600 border-transparent'
              ]"
              @click="contestFilter = filter.value"
            >
              {{ filter.label }}
            </div>
          </div>
          <TabsList>
            <TabsTrigger value="carousel">
              Vue carrousel
            </TabsTrigger>
            <TabsTrigger value="list">
              Vue liste
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="carousel" class="w-full">
          <ContestsSection
            v-model:status="contestFilter"
            :loading="isLoading"
            :contests="contests ?? []"
            @on-refresh="mutate"
          />
        </TabsContent>

        <TabsContent value="list" class="w-full">
          <ContestsList
            v-model:status="contestFilter"
            :loading="isLoading"
            :contests="contests ?? []"
            @on-refresh="mutate"
          />
        </TabsContent>
      </Tabs>
    </div>

    <div v-if="hasGrouping" class="bg-white rounded-3xl shadow-xl p-8 border border-gray-100">
      <div class="mb-8 text-center">
        <div class="inline-flex items-center justify-center w-16 h-16 gradient rounded-2xl mb-4">
          <Users class="w-8 h-8 text-white" />
        </div>
        <h2 class="text-3xl font-bold text-gray-900 mb-3">
          Challenges groupement
        </h2>
        <p class="text-lg text-gray-600 max-w-2xl mx-auto">
          Consultez les challenges Apodis en cours dans votre groupement
        </p>
        <div class="w-24 h-1 gradient rounded-full mx-auto mt-4" />
      </div>
      <div class="flex justify-center">
        <router-link :to="{name: 'grouping'}">
          <BaseButton primary>
            Voir les challenges groupement
          </BaseButton>
        </router-link>
      </div>
    </div>
  </div>
</template>

<style scoped>

</style>
